import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ExportFileTypeEnum } from '@shared/enum/export-file-type.enum';
import {
  ExportStockByLocationCriteria,
  ExportStockByProductCriteria,
  StoreExportCriteria,
  StoreExportOrderScheduleCriteria
} from '@shared/models';
import { GoodsReceiveExportCriteria } from '@shared/models/goods-receive.model';
import { ExportTypeEnum } from '@shared/models/import-export-button.model';
import { ProfitSharingExportCriteria } from '@shared/models/profit-sharing.model';
import { RewardCatalogExportCriteria } from '@shared/models/reward-catalog.model';
import { StockTransferGRExportCriteria } from '@shared/models/stock-transfer-gr.model';
import { StockTransferExportCriteria } from '@shared/models/stock-transfer.model';
import { DestroyService } from '@shared/services/destroy.service';
import { ExportReportService } from '@shared/services/export-report.service';
import { HoldAssortmentReportService } from '@shared/services/hold-assortment-report.service';
import { InventoryCountTransactionService } from '@shared/services/inventory-count-transaction.service';
import { MemberTransactionRewardsService } from '@shared/services/member-transaction-report.service';
import { OnlineCouponTransactionService } from '@shared/services/online-coupon-transaction.service';
import { PointEarnTransactionReport } from '@shared/services/point-earn-transaction.service';
import { ProfitSharingService } from '@shared/services/profit-sharing.service';
import { PromotionReportService } from '@shared/services/promotion-report.service';
import { PurchaseOrderService } from '@shared/services/purchase-order.service';
import { ReceiveOrderService } from '@shared/services/receive-order.service';
import { RewardOfferTransactionReportService } from '@shared/services/reward-offer-transaction-report.service';
import { RewardRedeemTransactionReportService } from '@shared/services/reward-redeem-transaction-report.service';
import { SalesTransactionConfidentialReportService } from '@shared/services/sales-transaction-confidential-report.service';
import { SalesTransactionReportService } from '@shared/services/sales-transaction-report.service';
import { SpecialPointReportService } from '@shared/services/special-point-report.service';
import { StockInformationService } from '@shared/services/stock-information.service';
import { StoreExportService } from '@shared/services/store-export.service';
import { StoreLocationReportService } from '@shared/services/store-location-report.service';
import { StoreReportService } from '@shared/services/store-report.service';
import { SupplierCouponReconcileReportService } from '@shared/services/supplier-coupon-reconcile-report.service';
import { TaskAssignmentReportService } from '@shared/services/task-assignment-report.service';
import { VoucherReportService } from '@shared/services/voucher-report.service';
import { Z8HoldRequestService } from '@shared/services/z8-hold-request.service';
import {
  dateTimeToDateOnlyString,
  dateTimeToMonthOnlyString,
  dateToStringCriteria,
  formatDate
} from '@shared/utils/date-util';

import { environment } from '../../../../../environments/environment';
import { ClaimRequestExportCriteria } from '../../../../pages/claim-request/model/claim-request.model';
import { FlashSaleDashboardReportService } from '../../../../pages/flash-sale-dashboard/services/flash-sale-dashboard-report.service';
import { GiftWithPurchaseService } from '../../../../pages/gift-with-purchase/service/gift-with-purchase.service';
import { HoldOrderReportService } from '../../../../pages/hold-order/services/hold-order-report.service';
import { ExportAdjustCriteria } from '../../../../pages/manage-stock/models/adjust-stock.model';
import { NewReportService } from '../../../../pages/new-reports/services/new-report.service';
import {
  OnlineCouponExportCriteria,
  OnlineCouponSummaryCriteria
} from '../../../../pages/online-coupon/model/online-coupon.model';
import { ReturnToWarehouseChargeService } from '../../../../pages/return-to-warehouse/services/return-to-warehouse-charge.service';
import { ReturnToWarehouseGRService } from '../../../../pages/return-to-warehouse/services/return-to-warehouse-gr.service';
import { ReturnToWarehouseOrderService } from '../../../../pages/return-to-warehouse/services/return-to-warehouse-order.service';
import { RewardExportCriteria } from '../../../../pages/reward/models/reward.model';
import { StockCountReportService } from '../../../../pages/stock-count/service/stock-count-report.service';
import { ToteReportService } from '../../../../pages/tote-information/services/tote-report.service';
import { DeliveryOrderExportPickingCriteria } from '../../../models/delivery-order.model';
import { OrderExportCriteria } from '../../../models/order.model';
import { HoldAssortmentExportCriteria } from '../../../models/permanent-hold.model';
import { ReceiveOrderExportCriteria } from '../../../models/receive-order.model';
import {
  AwaitingFirstLotExportCriteria,
  BillPaymentReportCriteria,
  BillPaymentTransactionReportCriteria,
  CashCountingReportCriteria,
  CashSettlementTransactionReportCriteria,
  CheckProductTagCriteria,
  DestroyDocumentReportCriteria,
  DestroyTransactionReportCriteria,
  InventoryAdjustmentRequestReportExportCriteria,
  InventoryCountTransactionReportExportCriteria,
  InventoryCountTransactionTempReportExportCriteria,
  InventoryVarianceCriteria,
  MaterialRequestTransactionCriteria,
  MemberRewardTransactionReportCriteria,
  MovingAverageReportExportCriteria,
  OnlineCouponCompensateCriteria,
  OnlineCouponTransactionCriteria,
  OrderProcessingHoldCriteria,
  PointBurnTransactionReportCriteria,
  PointEarnTransactionReportCriteria,
  PreOrderCatalogConfidentialCriteria,
  PreOrderCatalogExportCriteria,
  PreOrderCompensateReportCriteria,
  PromotionTransactionReportCriteria,
  PromotionV2ReportCriteria,
  ReceiveOrderExceedReportExportCriteria,
  ReduceToClearTransactionReportCriteria,
  SalesSummaryReportExportCriteria,
  SalesTaxReportCriteria,
  SalesTransactionExportCriteria,
  ShelfFixAssetReportCriteria,
  ShelfReplenishmentPhotoReviewCriteria,
  SpecialPointCompensateReportCriteria,
  StockCardExportCriteria,
  StoreLocationByDcReportCriteria,
  SupplierCouponReconcileReportCriteria,
  UserReportCriteria,
  VoucherExportCriteria
} from '../../../models/report.model';
import { AwaitingAllocationForGroupOrderService } from '../../../services/awaiting-allocation-for-group-order.service';
import { ClaimReportService } from '../../../services/claim-report.service';
import { GroupOrderReportService } from '../../../services/group-order-report.service';
import { MaterialRequestTransactionService } from '../../../services/material-request-transaction.service';
import { OnlineCouponCompensateService } from '../../../services/online-coupon-compensate.service';
import { PreOrderReportService } from '../../../services/pre-order-report.service';
import { PreOrderTransactionsReportService } from '../../../services/pre-order-transactions-report.service';
import { PromotionCompensateReportService } from '../../../services/promotion-compensate-report.service';
import { PromotionTransactionReportService } from '../../../services/promotion-transaction-report.service';
import { PromotionV2ReportService } from '../../../services/promotion-v2-report.service';
import { SpecialPointCompensateService } from '../../../services/special-point-compensate.service';
import { StoreService } from '../../../services/store.service';
import { dynamicReportAction } from './dynamic-reports.actions';

@Injectable()
export class DynamicReportsEffects {
  exportMoneyIn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.moneyIn),
      tap(action => this.logger.debug('@Effect Export Money In : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportMoneyIn(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportToteAdjustment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.toteAdjustment),
      tap(action => this.logger.debug('@Effect Export Tote Adjustment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.toteReportService.toteAdjustmentReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportHoldAssortment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.holdAssortment),
      tap(action => this.logger.debug('@Effect Export Hold Assortment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.store);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const attributes = [];
        if (payload.store) {
          attributes.push({
            id: 'store_code',
            name: 'Store Code',
            type: 'TEXT',
            operator: 'IN',
            values: payload.store.join(',')
          });
        }

        if (payload.storeType && payload.storeType.length !== 0) {
          attributes.push({
            id: 'store_type',
            name: 'Store Type',
            type: 'TEXT',
            operator: 'IN',
            values: payload.storeType.join(',')
          });
        }
        const reportCriteria = new HoldAssortmentExportCriteria({
          fileType: payload.fileType,
          attributes,
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null
        });
        return this.holdAssortmentReportService.reportExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportMemberRewardTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.memberRewardTransactions),
      tap(action =>
        this.logger.debug('@Effect Export Member Reward Transaction : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.store);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new MemberRewardTransactionReportCriteria({
          transactionDateFrom: formatDate(payload.memberRewardTransactionDateFrom, 'YYYY-MM-DD'),
          transactionDateTo: formatDate(payload.memberRewardTransactionDateTo, 'YYYY-MM-DD'),
          storeCodes: payload.store,
          fileType: payload.fileType
        });
        return this.memberTransactionRewardsService.exportMemberRewardTransaction(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportMemberRewardTransactionWithPrice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.memberRewardTransactionsWithPrice),
      tap(action =>
        this.logger.debug('@Effect Export Member Reward Transaction With Price : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.store);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new MemberRewardTransactionReportCriteria({
          transactionDateFrom: formatDate(payload.memberRewardTransactionDateFrom, 'YYYY-MM-DD'),
          transactionDateTo: formatDate(payload.memberRewardTransactionDateTo, 'YYYY-MM-DD'),
          storeCodes: payload.store,
          fileType: payload.fileType
        });
        return this.memberTransactionRewardsService.exportMemberRewardTransactionWithPrice(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportGroupOrderUnpickedItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.groupOrderUnpickedItem),
      tap(action =>
        this.logger.debug('@Effect Export Group Order Unpicked Item : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCode as any as string[]);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.groupOrderReportService.exportUnpickedItemReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportAwaitingAllocationForGroupOrderReportModalComponent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.awaitingAllocationForGroupOrder),
      tap(action =>
        this.logger.debug('@Effect Export Awaiting Allocation For GroupOrder : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        return this.awaitingAllocationForGroupOrderService.exportAwaitingAllocation(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportBillPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.billPayment),
      tap(action => this.logger.debug('@Effect Bill Payment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.store);
        if (validateLimitStore) return validateLimitStore;

        const reportCriteria = new BillPaymentReportCriteria({
          saleDateFrom: formatDate(payload.saleDateFrom, 'YYYY-MM-DD'),
          saleDateTo: formatDate(payload.saleDateTo, 'YYYY-MM-DD'),
          paymentDateFrom: formatDate(payload.paymentDateFrom, 'YYYY-MM-DD'),
          paymentDateTo: formatDate(payload.paymentDateTo, 'YYYY-MM-DD'),
          store: payload.store && payload.store.length > 0 ? payload.store.toString() : payload.store,
          status: payload.status && payload.status.length > 0 ? payload.status.toString() : null,
          fileType: payload.fileType
        });

        return this.storeReportService.exportBillPayment(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportOrderProcessingHold$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.orderProcessingHold),
      tap(action => this.logger.debug('@Effect Order Processing Hold : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.store as any);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const orderProcessingHoldCriteria = new OrderProcessingHoldCriteria({
          requestedDateFrom: formatDate(payload.requestedDateFrom, 'YYYY-MM-DD'),
          requestedDateTo: formatDate(payload.requestedDateTo, 'YYYY-MM-DD'),
          store: payload.store && payload.store.length > 0 ? payload.store.toString() : '',
          delistSuggestion:
            payload.delistSuggestion && payload.delistSuggestion.length > 0
              ? (payload.delistSuggestion as string[]).toString()
              : '',
          fileType: payload.fileType
        });
        return this.z8HoldRequestService.reportExport(orderProcessingHoldCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReceiveOrderExceed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.receiveOrderExceed),
      tap(action => this.logger.debug('@Effect Receive Order Exceed : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const receiveOrderExceedReportExportCriteria = new ReceiveOrderExceedReportExportCriteria({
          receiveOrderDateFrom: dateToStringCriteria(payload.receiveOrderDateFrom, true),
          receiveOrderDateTo: dateToStringCriteria(payload.receiveOrderDateTo, false)
        });
        return this.receiveOrderService.exceedExport(receiveOrderExceedReportExportCriteria).pipe(
          map(response => {
            const { timeFormat, prefix } = environment.fileName.exportReceiveOrderExceed;
            return dynamicReportAction.exportReportFileResponse({
              isSuccess: true,
              response,
              fileName: `${prefix} ${this.timeToExport(timeFormat)}.xlsx`
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPreOrderCompensate = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.preOrderCompensate),
      tap(action => this.logger.debug('@Effect Pre Order Compensate : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new PreOrderCompensateReportCriteria({
          transactionDateFrom: payload.transactionDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateFrom))
            : null,
          transactionDateTo: payload.transactionDateTo
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateTo))
            : null,
          storeCode: payload.storeCode,
          supplierCode: payload.supplierCode,
          fileType: payload.fileType
        });
        return this.preOrderTransactionsReportService.exportCompensate(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportOnlineCouponCompensate = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.onlineCouponCompensate),
      tap(action => this.logger.debug('@Effect Online Coupon Compensate : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCode);
        if (validateLimitStore) return validateLimitStore;

        return this.onlineCouponCompensateService.reportExport(payload as OnlineCouponCompensateCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportRedeemTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.redeemTransaction),
      tap(action => this.logger.debug('@Effect Redeem Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.rewardRedeemTransactionReportService.reportRewardRedeemTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportRewardTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.rewardTransaction),
      tap(action => this.logger.debug('@Effect Reward Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.rewardOfferTransactionReportService.reportRewardOfferTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPromotionCompensate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.promotionCompensate),
      tap(action => this.logger.debug('@Effect Promotion Compensate : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.promotionCompensateReportService.reportExport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSalesSummary = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.salesSummary),
      tap(action => this.logger.debug('@Effect Sales Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new SalesSummaryReportExportCriteria({
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          storeType: payload.storeType,
          fileType: payload.fileType
        });
        return this.newReportService.salesSummaryReportExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSalesTransaction = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.salesTransaction),
      tap(action => this.logger.debug('@Effect Sales Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new SalesTransactionExportCriteria({
          transactionDateFrom: payload.transactionDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateFrom))
            : null,
          transactionDateTo: payload.transactionDateTo
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateTo))
            : null,
          store: payload.store,
          salesType: payload.salesType?.length > 0 ? (payload.salesType as any as string[]).join(',') : null,
          fileType: payload.fileType
        });
        return this.salesTransactionService.reportExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSupplierCouponReconcile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.supplierCouponReconcile),
      tap(action => this.logger.debug('@Effect Supplier Coupon Reconcile : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.store);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        const reportCriteria = new SupplierCouponReconcileReportCriteria({
          transactionDateFrom: dateToStringCriteria(payload.transactionDateFrom, true),
          transactionDateTo: dateToStringCriteria(payload.transactionDateTo, false),
          storeNo: payload.store && payload.store.length > 0 ? payload.store.toString() : ''
        });
        return this.supplierCouponReconcileReportService.reportExport(reportCriteria).pipe(
          map(response => {
            const { timeFormat, prefix } = environment.fileName.exportSupplierCouponReconcile;
            return dynamicReportAction.exportReportFileResponse({
              isSuccess: true,
              response,
              fileName: `${prefix} ${this.timeToExport(timeFormat)}.xlsx`
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportVoucher$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.voucher),
      tap(action => this.logger.debug('@Effect Voucher : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const receiptDateFrom = payload.receiptDateFrom ? new Date(payload.receiptDateFrom) : null;
        const receiptDateTo = payload.receiptDateTo ? new Date(payload.receiptDateTo) : null;

        const voucherExportCriteria = new VoucherExportCriteria({
          receiptDateFrom: dateTimeToDateOnlyString(receiptDateFrom),
          receiptDateTo: dateTimeToDateOnlyString(receiptDateTo),
          fileType: payload.fileType
        });
        return this.voucherReportService.reportVoucher(voucherExportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCJXVoucher$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cjxVoucher),
      tap(action => this.logger.debug('@Effect CJX Voucher : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.voucherReportService.cjxReportVoucher(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportInventoryAdjustmentRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.inventoryAdjustmentRequest),
      tap(action => this.logger.debug('@Effect Inventory Adjustment Request : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const exportCriteria = new InventoryAdjustmentRequestReportExportCriteria({
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          storeType:
            payload.storeType && payload.storeType.length > 0 ? payload.storeType.toString() : payload.storeType,
          storeNo: payload.storeNo,
          exportBy: 'count',
          fileType: payload.fileType
        });
        return this.newReportService.exportInventoryAdjustmentRequest(exportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportDestroyDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.destroyDocument),
      tap(action => this.logger.debug('@Effect Destroy Document : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const destroyDocumentReportCriteria = new DestroyDocumentReportCriteria({
          docNo: payload.docNo
        });
        return this.destroyService.exportDestroyDocument(destroyDocumentReportCriteria).pipe(
          map(response => {
            const { timeFormat, prefix } = environment.fileName.exportDestroyDocument;
            return dynamicReportAction.exportReportFileResponse({
              isSuccess: true,
              response,
              fileName: `${prefix} ${this.timeToExport(timeFormat)}.pdf`
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportInventoryVariance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.inventoryVariance),
      tap(action => this.logger.debug('@Effect Inventory Variance : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const exportCriteria = new InventoryVarianceCriteria({
          createdDate: dateToStringCriteria(payload.createdDate, true),
          storeNo: payload.storeNo
        });
        return this.storeService.exportInventoryVarianceReport(exportCriteria).pipe(
          map(response => {
            return dynamicReportAction.exportReportFileResponse({
              isSuccess: true,
              response: response.fileBase64String,
              fileName: `${response.fileName}.pdf`,
              extension: ExportFileTypeEnum.PDF
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportDestroyTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.destroyTransaction),
      tap(action => this.logger.debug('@Effect Destroy Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload.storeNo);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        const destroyTransactionReportCriteria = new DestroyTransactionReportCriteria({
          createdDateFrom: payload.createdDateFrom ? dateTimeToDateOnlyString(payload.createdDateFrom) : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(payload.createdDateTo) : null,
          storeType: payload.storeType,
          storeNo: payload.storeNo,
          fileType: payload.fileType
        });
        return this.newReportService.exportDestroyTransaction(destroyTransactionReportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportInventoryCountTransactionTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.inventoryCountTransactions),
      tap(action => this.logger.debug('@Effect Inventory Count Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new InventoryCountTransactionReportExportCriteria({
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          storeType: payload.storeType && payload.storeType.length > 0 ? payload.storeType.toString() : null,
          storeCodes: payload.storeCodes ? payload.storeCodes.toString().split(',') : null,
          fileType: payload.fileType
        });
        return this.inventoryCountTransactionService.exportInventoryCountTransaction(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportInventoryCountTransactionWithPriceTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.inventoryCountTransactionsWithPrice),
      tap(action =>
        this.logger.debug('@Effect Inventory Count Transaction With Price : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const reportCriteria = new InventoryCountTransactionTempReportExportCriteria({
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          storeType: payload.storeType && payload.storeType.length > 0 ? payload.storeType.toString() : null,
          storeCodes: payload.storeCodes ? payload.storeCodes.toString().split(',') : null,
          fileType: payload.fileType
        });
        return this.inventoryCountTransactionService.exportInventoryCountTransactionWithPrice(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPreOrderTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.preOrderTransaction),
      tap(action => this.logger.debug('@Effect Pre Order Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.preOrderTransactionsReportService.exportTransactions(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPreOrderTransactionWithPrice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.preOrderTransactionWithPrice),
      tap(action =>
        this.logger.debug('@Effect Pre Order Transaction With Price : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.preOrderTransactionsReportService.exportTransactionsConfidential(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPromotion$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.promotion),
      tap(action => this.logger.debug('@Effect Promotion : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.promotionReportService.exportPromotion(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportShipment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.shipment),
      tap(action => this.logger.debug('@Effect Shipment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportShipment(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSpecialPointCompensate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.specialPointCompensate),
      tap(action => this.logger.debug('@Effect Export Special Point Compensate : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeNos as any as string[]);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        const reportCriteria = new SpecialPointCompensateReportCriteria({
          transactionDateFrom: payload.transactionDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateFrom))
            : null,
          transactionDateTo: payload.transactionDateTo
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateTo))
            : null,
          storeNos: payload.storeNos,
          supplierCode: payload.supplierCode,
          fileType: payload.fileType
        });
        return this.specialPointCompensateService.reportExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSpecialPoint$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.specialPoint),
      tap(action => this.logger.debug('@Effect Special Point : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimit = this.validateLimitListExceeded(payload.offerSupplierCode, 100, 'suppliers');

        if (validateLimit) {
          return validateLimit;
        }
        return this.specialPointReportService.specialPointExportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportResponse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.response),
      tap(action => this.logger.debug('@Effect Response : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.taskAssignmentReportService.responseExportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSurvey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.survey),
      tap(action => this.logger.debug('@Effect Survey : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.taskAssignmentReportService.surveyExportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReturnToWarehouseOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.returnToWarehouseOrder),
      tap(action => this.logger.debug('@Effect Return to Warehouse order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.returnToWarehouseOrderService.exportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReturnToWarehouseGR$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.returnToWarehouseGR),
      tap(action => this.logger.debug('@Effect Return to Warehouse GR : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.returnToWarehouseGRService.exportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReturnToWarehouseCharge$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.returnToWarehouseCharge),
      tap(action => this.logger.debug('@Effect Return to Warehouse Charge : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.returnToWarehouseChargeService.exportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportFlashSaleOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.flashSaleOrder),
      tap(action => this.logger.debug('@Effect Flash Sale Order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.flashSaleDashboardReportService.exportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  OnlineCouponTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.onlineCouponTransaction),
      tap(action =>
        this.logger.debug('@Effect Export Online Coupon Transaction Report : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const onlineCouponTransactionCriteria = new OnlineCouponTransactionCriteria({
          transactionDateFrom: formatDate(payload.transactionDateFrom, 'YYYY-MM-DD'),
          transactionDateTo: formatDate(payload.transactionDateTo, 'YYYY-MM-DD'),
          supplierCode: payload.supplierCode,
          fileType: payload.fileType,
          couponType: payload.couponType
        });

        return this.onlineCouponTransactionService.reportExport(onlineCouponTransactionCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportTaskAssignment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.taskAssignment),
      tap(action => this.logger.debug('@Effect Task : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportTaskAssignmentReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.order),
      tap(action => this.logger.debug('@Effect Order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCode);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const orderDateFrom = payload.orderDateFrom
          ? dateTimeToDateOnlyString(new Date(payload.orderDateFrom))
          : null;
        const orderDateTo = payload.orderDateTo ? dateTimeToDateOnlyString(new Date(payload.orderDateTo)) : null;

        const reportCriteria = new OrderExportCriteria({
          ...(orderDateFrom && { orderDateFrom }),
          ...(orderDateTo && { orderDateTo }),
          orderStatus: payload.orderStatus,
          orderType: payload.orderType,
          productType: payload.productType,
          warehouseCode: payload.warehouseCode,
          storeCode: payload.storeCode,
          orderFlow: payload.orderFlow,
          crossDockSupplier: payload.crossDockSupplier,
          groupOrder: false,
          fileType: payload.fileType
        });

        return this.newReportService.exportOrder(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSalesTransactionConfidential$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.salesTransactionConfidential),
      tap(action => this.logger.debug('@Effect Sales Transaction Confidential : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new SalesTransactionExportCriteria({
          transactionDateFrom: formatDate(payload.transactionDateFrom, 'YYYY-MM-DD'),
          transactionDateTo: formatDate(payload.transactionDateTo, 'YYYY-MM-DD'),
          store: payload.store && payload.store.length > 0 ? payload.store.toString() : null,
          salesType: payload.salesType && payload.salesType.length > 0 ? payload.salesType.toString() : null,
          fileType: payload.fileType
        });

        return this.salesTransactionConfidentialReportService.reportExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportHoldOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.holdOrder),
      tap(action => this.logger.debug('@Effect Hold Order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.holdOrderReportService.exportReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportRefundSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.refundSummary),
      tap(action => this.logger.debug('@Effect Refund Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportRefundSummary(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportRefundTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.refundTransaction),
      tap(action => this.logger.debug('@Effect Refund Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportRefundTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportDeliveryOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.deliveryOrder),
      tap(action => this.logger.debug('@Effect Delivery Order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const { timeFormat, prefix } = environment.fileName.exportDo;
        const reportCriteria = new DeliveryOrderExportPickingCriteria({
          ...payload,
          orderDateFrom: payload.orderDateFrom ? dateTimeToDateOnlyString(new Date(payload.orderDateFrom)) : null,
          orderDateTo: payload.orderDateTo ? dateTimeToDateOnlyString(new Date(payload.orderDateTo)) : null,
          pickDateFrom: payload.pickDateFrom ? dateTimeToDateOnlyString(new Date(payload.pickDateFrom)) : null,
          pickDateTo: payload.pickDateTo ? dateTimeToDateOnlyString(new Date(payload.pickDateTo)) : null,
          requestedDeliveryDateFrom: payload.requestedDeliveryDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.requestedDeliveryDateFrom))
            : null,
          requestedDeliveryDateTo: payload.requestedDeliveryDateTo
            ? dateTimeToDateOnlyString(new Date(payload.requestedDeliveryDateTo))
            : null
        });
        return this.newReportService.exportDeliveryOrder(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReceiveOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.receiveOrder),
      tap(action => this.logger.debug('@Effect Receive Order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new ReceiveOrderExportCriteria({
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          orderType: payload.orderType,
          storeNo: payload.storeNo,
          warehouseCode: payload.warehouseCode,
          fileType: payload.fileType
        });
        return this.newReportService.exportReceiveOrderReport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportClaim$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.claim),
      tap(action => this.logger.debug('@Effect Claim : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new ClaimRequestExportCriteria({
          requestedDateFrom: payload.requestedDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.requestedDateFrom))
            : null,
          requestedDateTo: payload.requestedDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.requestedDateTo))
            : null,
          claimStatus: this.convertArrayFilter(payload.claimStatus),
          warehouseCodes: this.convertArrayFilter(payload.warehouseCodes),
          fileType: payload.fileType
        });
        return this.claimService.exportClaimRequest(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  awaitingFirstLot$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.awaitingFirstLot),
      tap(action => this.logger.debug('@Effect Awaiting First Lot : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new AwaitingFirstLotExportCriteria({
          openDateFrom: payload.openDateFrom ? dateTimeToDateOnlyString(new Date(payload.openDateFrom)) : null,
          openDateTo: payload.openDateTo ? dateTimeToDateOnlyString(new Date(payload.openDateTo)) : null,
          storeType: payload.storeType,
          region: payload.region,
          state: payload.state,
          fileType: payload.fileType
        });
        return this.newReportService.exportAwaitingFirstLot(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockByProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockByProduct),
      tap(action => this.logger.debug('@Effect Stock By Product : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimit = this.validateLimitListExceeded(payload.locations, 100, 'locations');
        if (validateLimit) {
          return validateLimit;
        }
        const reportCriteria = new ExportStockByProductCriteria({
          ...payload,
          locations: payload.locations ? payload.locations : null,
          exportBy: ExportTypeEnum.PRODUCT
        });
        return this.newReportService.stockByProductExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockByLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockByLocation),
      tap(action => this.logger.debug('@Effect Stock By Location : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new ExportStockByLocationCriteria({
          ...payload,
          location: payload.location ? [payload.location] : null,
          productType: this.convertArrayFilter(payload.productType),
          exportBy: ExportTypeEnum.LOCATION
        });

        return this.newReportService.stockByLocationExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockAdjustment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockAdjustment),
      tap(action => this.logger.debug('@Effect Stock Adjustment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new ExportAdjustCriteria({
          locationTypes: payload.locationTypes,
          adjustTypes: payload.adjustTypes,
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          fileType: payload.fileType
        });

        return this.newReportService.exportStockAdjustment(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockCard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockCard),
      tap(action => this.logger.debug('@Effect Stock Card : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new StockCardExportCriteria({
          ...payload,
          location: payload.location.code,
          locationType: payload.location.locationType,
          documentDateFrom: payload.documentDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.documentDateFrom))
            : null,
          documentDateTo: payload.documentDateTo ? dateTimeToDateOnlyString(new Date(payload.documentDateTo)) : null,
          exportBy: ExportTypeEnum.CARD
        });

        return this.newReportService.stockCardExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportTransferOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.transferOrder),
      tap(action => this.logger.debug('@Effect Transfer Order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new StockTransferExportCriteria({
          ...payload,
          orderDateFrom: payload.orderDateFrom ? dateTimeToDateOnlyString(new Date(payload.orderDateFrom)) : null,
          orderDateTo: payload.orderDateTo ? dateTimeToDateOnlyString(new Date(payload.orderDateTo)) : null,
          deliveryDateFrom: payload.deliveryDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.deliveryDateFrom))
            : null,
          deliveryDateTo: payload.deliveryDateTo ? dateTimeToDateOnlyString(new Date(payload.deliveryDateTo)) : null
        });

        return this.exportReportService.exportStockTransfer(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportTransferGoodsReceive$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.transferGoodsReceive),
      tap(action => this.logger.debug('@Effect Transfer Goods Receive : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new StockTransferGRExportCriteria({
          ...payload,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null
        });

        return this.exportReportService.onExportStockTransferGR(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.user),
      tap(action => this.logger.debug('@Effect User : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const merchantNo = environment.prefixCompany === 'TD' ? '000001' : payload.merchantNo;
        const reportCriteria = new UserReportCriteria({
          ...payload,
          merchantNo
        });

        return this.newReportService.exportUserReport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportProfitSharing$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.profitSharing),
      tap(action => this.logger.debug('@Effect Profit Sharing : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new ProfitSharingExportCriteria({
          ...payload,
          scheduleFrom: dateTimeToMonthOnlyString(new Date(payload.scheduleFrom), true),
          scheduleTo: dateTimeToMonthOnlyString(new Date(payload.scheduleTo), false)
        });

        return this.profitSharingService.export(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreConsultant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeConsultant),
      tap(action => this.logger.debug('@Effect Store Consultant : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportStoreConsultant(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStore$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.store),
      tap(action => this.logger.debug('@Effect Store : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new StoreExportCriteria({
          ...payload,
          brand: this.convertArrayFilter(payload.brand),
          status: this.convertArrayFilter(payload.status)
        });

        return this.storeExportService.exportStore(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCompanyAssortment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.companyAssortment),
      tap(action => this.logger.debug('@Effect Company TD Assortment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportCompanyAssortment(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.product),
      tap(action => this.logger.debug('@Effect Product Report : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportProduct(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportProductConfidential$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.productConfidential),
      tap(action => this.logger.debug('@Effect Product Report Confidential: ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportProductConfidential(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportProductRetailPriceConfidential$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.productRetailPriceConfidential),
      tap(action =>
        this.logger.debug('@Effect Product Retail Price Confidential: ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        return this.newReportService.exportProductRetailPriceConfidential(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportProductEcommerce$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.productEcommerce),
      tap(action => this.logger.debug('@Effect Product Ecommerce: ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportProductEcommerce(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCompanyAssortmentConfidential$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.companyAssortmentConfidential),
      tap(action => this.logger.debug('@Effect Company Assortment Confidential : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportCompanyAssortmentConfidential(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportProductByLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.productByLocation),
      tap(action => this.logger.debug('@Effect Product By Location : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportProductByLocation(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportDcAssortment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.dcAssortment),
      tap(action => this.logger.debug('@Effect DC Assortment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportDcAssortment(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportMovingAverage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.movingAverage),
      tap(action => this.logger.debug('@Effect Moving Average : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new MovingAverageReportExportCriteria({
          ...payload,
          createdDateFrom: payload.createdDateFrom ? dateTimeToDateOnlyString(payload.createdDateFrom) : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(payload.createdDateTo) : null,
          fileType: payload.fileType
        });

        return this.newReportService.exportMovingAverage(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreConfidential$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeConfidential),
      tap(action => this.logger.debug('@Effect Store Confidential : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new StoreExportCriteria({
          ...payload,
          brand: this.convertArrayFilter(payload.brand),
          status: this.convertArrayFilter(payload.status)
        });

        return this.storeExportService.exportStoreConfidential(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPreOrderCatalog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.preOrderCatalog),
      tap(action => this.logger.debug('@Effect Pre Order Catalog : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new PreOrderCatalogExportCriteria({
          ...payload,
          status: payload.status ? payload.status.join(',') : null,
          effectiveDateFrom: payload.exportEffectiveDateFrom
            ? dateTimeToDateOnlyString(payload.exportEffectiveDateFrom)
            : null,
          effectiveDateTo: payload.exportEffectiveDateTo
            ? dateTimeToDateOnlyString(payload.exportEffectiveDateTo)
            : null,
          expireDateFrom: payload.exportExpireDateFrom
            ? dateTimeToDateOnlyString(payload.exportExpireDateFrom)
            : null,
          expireDateTo: payload.exportExpireDateTo ? dateTimeToDateOnlyString(payload.exportExpireDateTo) : null,
          validDateFrom: payload.exportValidDateFrom ? dateTimeToDateOnlyString(payload.exportValidDateFrom) : null,
          validDateTo: payload.exportValidDateTo ? dateTimeToDateOnlyString(payload.exportValidDateTo) : null,
          fileType: payload.fileType
        });

        return this.preorderReportService.exportCatalog(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreOrderSchedule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeOrderSchedule),
      tap(action => this.logger.debug('@Effect Store Order Schedule : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new StoreExportOrderScheduleCriteria({
          ...payload,
          scheduleType: this.convertArrayFilter(payload.scheduleType),
          warehouseCode: this.convertArrayFilter(payload.warehouseCode),
          status: this.convertArrayFilter(payload.status)
        });

        return this.storeExportService.exportStoreOrderSchedule(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPreOrderCatalogConfidential$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.preOrderCatalogConfidential),
      tap(action => this.logger.debug('@Effect Pre Order Catalog Confidential : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new PreOrderCatalogConfidentialCriteria({
          ...payload,
          status: payload.status && payload.status.length > 0 ? payload.status.join(',') : null
        });

        return this.preorderReportService.reportConfidential(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreLocationByDc$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeLocationByDc),
      tap(action => this.logger.debug('@Effect Store Order Schedule : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new StoreLocationByDcReportCriteria({
          ...payload
        });
        return this.storeLocationReportService.exportByDc(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportShelfFixAsset$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.shelfFixAsset),
      tap(action => this.logger.debug('@Effect Shelf Fix Asset : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new ShelfFixAssetReportCriteria({
          selected: payload.no,
          status: payload.status,
          fileType: payload.fileType
        });
        return this.newReportService.exportShelf(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportShelfInventory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.shelfInventory),
      tap(action => this.logger.debug('@Effect Shelf Inventory : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportShelfInventory(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreAssortment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeAssortment),
      tap(action => this.logger.debug('@Effect Store Assortment : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeNo);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.exportReportService.exportByStore(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReward$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.reward),
      tap(action => this.logger.debug('@Effect Reward : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new RewardExportCriteria({
          ...payload,
          validDateFrom: payload.validDateFrom ? dateTimeToDateOnlyString(new Date(payload.validDateFrom)) : null,
          validDateTo: payload.validDateTo ? dateTimeToDateOnlyString(new Date(payload.validDateTo)) : null,
          offerFromDateFrom: payload.offerFromDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.offerFromDateFrom))
            : null,
          offerFromDateTo: payload.offerFromDateTo
            ? dateTimeToDateOnlyString(new Date(payload.offerFromDateTo))
            : null,
          offerToDateFrom: payload.offerToDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.offerToDateFrom))
            : null,
          offerToDateTo: payload.offerToDateTo ? dateTimeToDateOnlyString(new Date(payload.offerToDateTo)) : null
        });

        return this.newReportService.exportReward(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPointEarnTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.pointEarnTransaction),
      tap(action => this.logger.debug('@Effect Point Earn Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new PointEarnTransactionReportCriteria({
          ...payload,
          transactionDateFrom: payload.transactionDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateFrom))
            : null,
          transactionDateTo: payload.transactionDateTo
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateTo))
            : null,
          storeCodes: payload.storeCodes ? payload.storeCodes.toString().split(',') : null
        });
        return this.pointEarnTransactionReportService.reportExport(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPointBurnTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.pointBurnTransaction),
      tap(action => this.logger.debug('@Effect Point Burn Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCode);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const rawData = new PointBurnTransactionReportCriteria({
          transactionDateFrom: dateToStringCriteria(payload.transactionDateFrom),
          transactionDateTo: dateToStringCriteria(payload.transactionDateTo, false),
          storeCode: payload.storeCode ? payload.storeCode.toString().split(',') : null,
          fileType: payload.fileType
        });

        return this.newReportService.exportPointBurnTransaction(rawData).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportOnlineCoupon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.onlineCoupon),
      tap(action => this.logger.debug('@Effect Online Coupon : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new OnlineCouponExportCriteria({
          ...payload,
          offerDateFrom: payload.offerDateFrom ? dateTimeToDateOnlyString(new Date(payload.offerDateFrom)) : null,
          offerDateTo: payload.offerDateTo ? dateTimeToDateOnlyString(new Date(payload.offerDateTo)) : null,
          redeemDateFrom: payload.redeemDateFrom ? dateTimeToDateOnlyString(new Date(payload.redeemDateFrom)) : null,
          redeemDateTo: payload.redeemDateTo ? dateTimeToDateOnlyString(new Date(payload.redeemDateTo)) : null
        });

        return this.exportReportService.exportOnlineCoupon(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportOnlineCouponSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.onlineCouponSummary),
      tap(action => this.logger.debug('@Effect Online Coupon Summary: ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new OnlineCouponSummaryCriteria({
          ...payload,
          docNo: payload.docNo ? payload.docNo : null,
          offerDateFrom: payload.offerDateFrom ? dateTimeToDateOnlyString(new Date(payload.offerDateFrom)) : null,
          offerDateTo: payload.offerDateTo ? dateTimeToDateOnlyString(new Date(payload.offerDateTo)) : null,
          redeemDateFrom: payload.redeemDateFrom ? dateTimeToDateOnlyString(new Date(payload.redeemDateFrom)) : null,
          redeemDateTo: payload.redeemDateTo ? dateTimeToDateOnlyString(new Date(payload.redeemDateTo)) : null,
          couponType: payload.couponType,
          status: payload.status,
          fileType: payload.fileType
        });

        return this.exportReportService.exportOnlineCouponSummary(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportMemberRewardCatalog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.memberRewardCatalog),
      tap(action => this.logger.debug('@Effect Member Reward Catalog : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const reportCriteria = new RewardCatalogExportCriteria({
          ...payload,
          effectiveDateFrom: payload.effectiveDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.effectiveDateFrom))
            : null,
          effectiveDateTo: payload.effectiveDateTo
            ? dateTimeToDateOnlyString(new Date(payload.effectiveDateTo))
            : null,
          expireDateFrom: payload.expireDateFrom ? dateTimeToDateOnlyString(new Date(payload.expireDateFrom)) : null,
          expireDateTo: payload.expireDateTo ? dateTimeToDateOnlyString(new Date(payload.expireDateTo)) : null,
          validDateFrom: payload.validDateFrom ? dateTimeToDateOnlyString(new Date(payload.validDateFrom)) : null,
          validDateTo: payload.validDateTo ? dateTimeToDateOnlyString(new Date(payload.validDateTo)) : null
        });

        const { timeFormat, prefix } = environment.fileName.exportRewardCatalogReport;
        return this.newReportService.exportRewardCatalog(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPurchaseRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.purchaseRequest),
      tap(action => this.logger.debug('@Effect Purchase Request : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportPr(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPurchaseOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.purchaseOrder),
      tap(action => this.logger.debug('@Effect Purchase Order : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.purchaseOrderService.exportPo(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportGoodsReceive$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.goodsReceive),
      tap(action => this.logger.debug('@Effect Goods Receive : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitLocation = this.validateLimitListExceeded(payload?.locations, 100, 'locations');
        if (validateLimitLocation) {
          return validateLimitLocation;
        }
        const reportCriteria = new GoodsReceiveExportCriteria({
          ...payload,
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null
        });

        return this.newReportService.exportGoodsReceive(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPreOrderCreditRemaining$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.preOrderCreditRemaining),
      tap(action => this.logger.debug('@Effect Pre Order Credit Remaining : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportPreOrderCreditRemaining(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreTote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeTote),
      tap(action => this.logger.debug('@Effect Store Tote : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.exportReportService.exportStoreTote(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportStoreToteFine$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeToteFine),
      tap(action => this.logger.debug('@Effect Store Tote Fine : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitLocation = this.validateLimitListExceeded(payload?.location, 100, 'locations');
        if (validateLimitLocation) {
          return validateLimitLocation;
        }

        return this.exportReportService.exportStoreToteFine(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCrossDockInvoice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.crossDockInvoice),
      tap(action => this.logger.debug('@Effect Cross Dock Invoice : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportCrossDockInvoice(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportManualChangeDeliveryStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.manualChangeDeliveryStatus),
      tap(action => this.logger.debug('@Effect Manual Change Delivery Status : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportManualChangeDeliveryStatus(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreApprovalBypass$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeApprovalBypass),
      tap(action => this.logger.debug('@Effect Store Approval Bypass : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        payload = {
          ...payload,
          storeCodes: payload.storeCodes && payload.storeCodes.length > 0 ? payload.storeCodes : null
        };

        return this.exportReportService.exportStoreApprovalBypass(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPromotionV2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.promotionV2),
      tap(action => this.logger.debug('@Effect Export Promotion V2 report : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const criteria = new PromotionV2ReportCriteria({
          validDateFrom: payload.validDateFrom ? dateTimeToDateOnlyString(new Date(payload.validDateFrom)) : null,
          validDateTo: payload.validDateTo ? dateTimeToDateOnlyString(new Date(payload.validDateTo)) : null,
          type: payload.type ?? undefined,
          discountType: payload.discountType ?? undefined,
          memberOnly:
            payload.memberOnly && (payload.memberOnly as any).length === 1 ? payload.memberOnly[0] : undefined,
          status: payload.status && payload.status.length > 0 ? payload.status : undefined,
          fileType: payload.fileType
        });

        return this.promotionV2ReportService.exportPromotionV2(criteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReduceToClearSummaryReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.exportReportReduceToClearSummary),
      tap(action => this.logger.debug('@Effect Reduce to Clear Summary Report: ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        payload = {
          ...payload,
          storeCodes: payload.storeCodes && payload.storeCodes.length > 0 ? payload.storeCodes : null,
          status: payload.status && payload.status.length > 0 ? payload.status : null
        };
        return this.newReportService.exportReduceToClearSummary(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportDestroyTransactionReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.exportReportDestroyTransaction),
      tap(action => this.logger.debug('@Effect Destroy Transaction Report: ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.exportReportService.exportDestroyTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReduceToClearTransactionReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.exportReduceToClearTransaction),
      tap(action =>
        this.logger.debug('@Effect Reduce To Clear Transaction Report: ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const reportCriteria = new ReduceToClearTransactionReportCriteria({
          ...payload,
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null
        });
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportReduceToClearTransaction(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPromotionTransactionReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.exportPromotionTransaction),
      tap(action =>
        this.logger.debug('@Effect Export Promotion Transaction report : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        const validateLimitOfferBy = this.validateLimitListExceeded(payload?.supplierCode, 100, 'suppliers');
        if (validateLimitStore || validateLimitOfferBy) {
          return validateLimitStore || validateLimitOfferBy;
        }

        const criteria = new PromotionTransactionReportCriteria({
          receiptDateFrom: payload.receiptDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.receiptDateFrom))
            : null,
          receiptDateTo: payload.receiptDateTo ? dateTimeToDateOnlyString(new Date(payload.receiptDateTo)) : null,
          storeCodes: payload.storeCodes && payload.storeCodes.length ? payload.storeCodes : null,
          supplierCode: payload.supplierCode && payload.supplierCode.length ? payload.supplierCode : null,
          fileType: payload.fileType
        });

        return this.promotionTransactionReportService.exportPromotionTransaction(criteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCJXSalesTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cjxSalesTransaction),
      tap(action => this.logger.debug('@Effect CJX Sales Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.salesTransactionService.reportCJXSalesTransactionReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportMaterialRequestTransactionReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.materialRequestTransaction),
      tap(action =>
        this.logger.debug('@Effect Export Material Request Transaction report : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const criteria = new MaterialRequestTransactionCriteria({
          transactionDateFrom: payload.transactionDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateFrom))
            : null,
          transactionDateTo: payload.transactionDateTo
            ? dateTimeToDateOnlyString(new Date(payload.transactionDateTo))
            : null,
          storeCodes: payload.storeCodes && payload.storeCodes.length ? payload.storeCodes : null,
          fileType: payload.fileType
        });

        return this.materialRequestTransactionService.exportMaterialRequestTransaction(criteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCashSettlementTransactionBeforeGoLive$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cashSettlementTransactionBeforeGoLive),
      tap(action =>
        this.logger.debug('@Effect Cash Settlement Transaction Before Go Live: ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new CashSettlementTransactionReportCriteria({
          saleScheduleDateFrom: formatDate(payload.saleScheduleDateFrom, 'YYYY-MM-DD'),
          saleScheduleDateTo: formatDate(payload.saleScheduleDateTo, 'YYYY-MM-DD'),
          transferDateFrom: formatDate(payload.transferDateFrom, 'YYYY-MM-DD'),
          transferDateTo: formatDate(payload.transferDateTo, 'YYYY-MM-DD'),
          storeCodes: payload.storeCodes && payload.storeCodes.length ? payload.storeCodes : null,
          settlementStatuses:
            payload.settlementStatuses && payload.settlementStatuses.length ? payload.settlementStatuses : null,
          fileType: payload.fileType
        });

        return this.exportReportService.exportCashSettlementTransactionBeforeGoLive(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCashSettlementTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cashSettlementTransaction),
      tap(action => this.logger.debug('@Effect Cash Settlement Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new CashSettlementTransactionReportCriteria({
          saleScheduleDateFrom: formatDate(payload.saleScheduleDateFrom, 'YYYY-MM-DD'),
          saleScheduleDateTo: formatDate(payload.saleScheduleDateTo, 'YYYY-MM-DD'),
          transferDateFrom: formatDate(payload.transferDateFrom, 'YYYY-MM-DD'),
          transferDateTo: formatDate(payload.transferDateTo, 'YYYY-MM-DD'),
          storeCodes: payload.storeCodes && payload.storeCodes.length ? payload.storeCodes : null,
          settlementStatuses:
            payload.settlementStatuses && payload.settlementStatuses.length ? payload.settlementStatuses : null,
          fileType: payload.fileType
        });

        return this.exportReportService.exportCashSettlementTransaction(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCashCounting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cashCounting),
      tap(action => this.logger.debug('@Effect Cash Settlement Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new CashCountingReportCriteria({
          saleScheduleDateFrom: formatDate(payload.saleScheduleDateFrom, 'YYYY-MM-DD'),
          saleScheduleDateTo: formatDate(payload.saleScheduleDateTo, 'YYYY-MM-DD'),
          storeCodes: payload.storeCodes && payload.storeCodes.length ? payload.storeCodes : null,
          fileType: payload.fileType
        });

        return this.exportReportService.exportCashCounting(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSupplier$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.supplier),
      tap(action => this.logger.debug('@Effect Supplier : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportSupplier(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportReceiptCoupon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.receiptCoupon),
      tap(action => this.logger.debug('@Effect Receipt Coupon : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportReceiptCouponTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockCountByQISummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockCountByQISummary),
      tap(action => this.logger.debug('@Effect Stock Count by QI Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStockCountByQISummary(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockCountByQITransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockCountByQITransaction),
      tap(action => this.logger.debug('@Effect Stock Count by QI Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStockCountByQITransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreAvailableAdjustRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeAvailableAdjustmentRequest),
      tap(action =>
        this.logger.debug('@Effect Store Available Adjustment Request : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStoreAvailableAdjustmentRequest(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockCountAssignmentSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockCountAssignmentSummary),
      tap(action => this.logger.debug('@Effect Stock Count Assignment Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStockCountAssignmentSummary(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockCountAssignmentTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockCountAssignmentTransaction),
      tap(action =>
        this.logger.debug('@Effect Stock Count Assignment Transaction : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStockCountAssignmentTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockCountByStaffTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockCountByStaffTransaction),
      tap(action =>
        this.logger.debug('@Effect Stock Count By Staff Transaction : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStockCountByStaffTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockCountByQIDailySummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockCountByQIDailySummary),
      tap(action => this.logger.debug('@Effect Stock Count by QI Daily Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.stockCountReportService.getStockCountByQIDailySummary(payload).pipe(
          map(response => {
            return dynamicReportAction.exportReportFileResponse({
              isSuccess: true,
              response: response.content,
              fileName: response.fileName,
              extension: ExportFileTypeEnum.PDF
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreExpense$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeExpense),
      tap(action => this.logger.debug('@Effect Store Expense : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStoreExpense(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStoreOtherIncome$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeOtherIncome),
      tap(action => this.logger.debug('@Effect Store Other Income : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStoreOtherIncome(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSalePerformance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.salePerformance),
      tap(action => this.logger.debug('@Effect Sale Performance : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportSalePerformance(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCountItemInTote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.countItemInTote),
      tap(action => this.logger.debug('@Effect Count Item in Tote : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportCountItemInTote(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSupplierCouponIssueTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.supplierCouponIssueTransaction),
      tap(action =>
        this.logger.debug('@Effect Supplier Coupon Issue Transaction : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportSupplierCouponIssueTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSupplierCouponTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.supplierCouponTransaction),
      tap(action => this.logger.debug('@Effect Supplier Coupon Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportSupplierCouponTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportShiftRecord$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.shiftRecord),
      tap(action => this.logger.debug('@Effect Shift Record : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportShiftRecord(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportMoneyInOut$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.moneyInOut),
      tap(action => this.logger.debug('@Effect Money In-Out : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportMoneyInOut(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCJXSalesSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cjxSalesSummary),
      tap(action => this.logger.debug('@Effect CJX Sales Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportCJXSalesSummary(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCafeBillOfMaterialsCost$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cafeBillOfMaterialsCost),
      tap(action => this.logger.debug('@Effect Cafe Bill of Materials Cost : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportCafeBillOfMaterialsCost(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCafeSalesAndProfit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cafeSalesAndProfit),
      tap(action => this.logger.debug('@Effect Cafe Sales And Profit : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportCafeSalesAndProfit(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportSalesTax$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.salesTax),
      tap(action => this.logger.debug('@Effect Sales Tax : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        const reportCriteria = new SalesTaxReportCriteria({
          ...payload,
          asOfMonthFrom: dateTimeToMonthOnlyString(new Date(payload.asOfMonthFrom), true),
          asOfMonthTo: dateTimeToMonthOnlyString(new Date(payload.asOfMonthTo), false),
          language: 'EN'
        });

        return this.newReportService.exportSalesTax(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportMemberProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.memberProfile),
      tap(action => this.logger.debug('@Effect Member Profile Report : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportMemberProfileReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportPointActivityProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.pointActivity),
      tap(action => this.logger.debug('@Effect Point Activity Report : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportPointActivityReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCJXShelfInventory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cjxShelfInventory),
      tap(action => this.logger.debug('@Effect CJX Shelf Inventory : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportCJXShelfInventory(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCJXMemberRewardTransactions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cjxMemberRewardTransactions),
      tap(action => this.logger.debug('@Effect CJX Member Reward Transactions : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportCJXMemberRewardTransactions(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportGiftWithPurchase$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.giftWithPurchase),
      tap(action => this.logger.debug('@Effect Gift with Purchase : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportGiftWithPurchase(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportGiftWithPurchaseTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.giftWithPurchaseTransaction),
      tap(action => this.logger.debug('@Effect Gift with Purchase Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        const validateLimitOfferBy = this.validateLimitListExceeded(payload?.supplierCode, 100, 'suppliers');
        if (validateLimitStore || validateLimitOfferBy) {
          return validateLimitStore || validateLimitOfferBy;
        }
        return this.giftWithPurchaseService.exportGiftWithPurchaseTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  storeStockRecall = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeStockRecall),
      tap(action => this.logger.debug('@Effect Store Stock Recall : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportStoreStockRecall(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  recallStockPackingConfirmation = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.recallStockPackingConfirmation),
      tap(action =>
        this.logger.debug('@Effect Recall Stock Packing Confirmation : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        return this.newReportService.exportRecallStockPackingConfirmation(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockRefillSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockRefillSummary),
      tap(action => this.logger.debug('@Effect Stock Refill Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStockRefillSummary(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportStockRefillPerformance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.stockRefillPerformance),
      tap(action => this.logger.debug('@Effect Stock Refill Performance : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportStockRefillPerformance(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportCafeOrderProcessingPerformance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.cafeOrderProcessingPerformance),
      tap(action =>
        this.logger.debug('@Effect Cafe Order Processing Performance : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportCafeOrderProcessingPerformance(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  recallStockCheckConfirmation = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.recallStockCheckConfirmation),
      tap(action => this.logger.debug('@Effect Recall Stock Check Confirmation : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportRecallStockCheckConfirmation(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  recallCreditNote = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.recallCreditNote),
      tap(action => this.logger.debug('@Effect Recall Credit Note : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportRecallCreditNote(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });
  exportQRPaymentTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.qrPaymentTransaction),
      tap(action => this.logger.debug('@Effect QR Payment Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportQRPaymentTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportDispatchTote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.dispatchTote),
      tap(action => this.logger.debug('@Effect Dispatch Tote : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportDispatchTote(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportReceiveTote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.receiveTote),
      tap(action => this.logger.debug('@Effect Receive Tote : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportReceiveTote(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportReturnTote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.returnTote),
      tap(action => this.logger.debug('@Effect Return Tote : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportReturnTote(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportShelfReplenishmentTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.shelfReplenishmentTransaction),
      tap(action => this.logger.debug('@Effect Shelf Replenishment Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportShelfReplenishmentTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportShelfReplenishmentSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.shelfReplenishmentSummary),
      tap(action => this.logger.debug('@Effect Shelf Replenishment Summary : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportShelfReplenishmentSummary(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportCollectExpiredPromotionTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.collectExpiredPromotionTag),
      tap(action => this.logger.debug('@Effect Collect Expired Promotion Tag : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportCollectExpiredPromotionTag(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportShelfReplenishmentPhotoReview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.shelfReplenishmentPhotoReviewAction),
      tap(action =>
        this.logger.debug('@Effect Shelf Replenishment photo review : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new ShelfReplenishmentPhotoReviewCriteria({
          receivedDateFrom: payload.receivedDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.receivedDateFrom))
            : null,
          receivedDateTo: payload.receivedDateTo ? dateTimeToDateOnlyString(new Date(payload.receivedDateTo)) : null,
          verifiedDateFrom: payload.verifiedDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.verifiedDateFrom))
            : null,
          verifiedDateTo: payload.verifiedDateTo ? dateTimeToDateOnlyString(new Date(payload.verifiedDateTo)) : null,
          storeCodes: payload.storeCodes && payload.storeCodes.length ? payload.storeCodes : null,
          fileType: payload.fileType
        });

        return this.newReportService.exportShelfReplenishmentPhotoReview(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportCheckProductTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.checkProductTag),
      tap(action => this.logger.debug('@Effect Check Product Tag : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new CheckProductTagCriteria({
          createdDateFrom: payload.createdDateFrom
            ? dateTimeToDateOnlyString(new Date(payload.createdDateFrom))
            : null,
          createdDateTo: payload.createdDateTo ? dateTimeToDateOnlyString(new Date(payload.createdDateTo)) : null,
          storeCodes: payload.storeCodes && payload.storeCodes.length ? payload.storeCodes : null,
          fileType: payload.fileType
        });

        return this.newReportService.exportCheckProductTag(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportStoreGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.storeGroup),
      tap(action => this.logger.debug('@Effect Store Group : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportStoreGroup(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportBillPaymentTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.billPaymentTransaction),
      tap(action => this.logger.debug('@Effect Bill Payment Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        const reportCriteria = new BillPaymentTransactionReportCriteria({
          createdDateFrom: formatDate(payload.createdDateFrom, 'YYYY-MM-DD'),
          createdDateTo: formatDate(payload.createdDateTo, 'YYYY-MM-DD'),
          saleScheduleDateFrom: formatDate(payload.saleScheduleDateFrom, 'YYYY-MM-DD'),
          saleScheduleDateTo: formatDate(payload.saleScheduleDateTo, 'YYYY-MM-DD'),
          storeCodes: payload.storeCodes,
          fileType: payload.fileType
        });

        return this.newReportService.exportBillPaymentTransaction(reportCriteria).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportSupplierPartnerCoupon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.supplierPartnerCoupon),
      tap(action => this.logger.debug('@Effect Supplier Partner Coupon : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportSupplierPartnerCoupon(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportLinemanProduct = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.linemanProduct),
      tap(action => this.logger.debug('@Effect LINEMAN Product Report : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportLinemanProduct(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportLinemanSalesReconcileTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.linemanSalesReconcileTransaction),
      tap(action =>
        this.logger.debug('@Effect Lineman Sales Reconcile Transaction : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportLinemanSalesReconcileTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportLinemanOrderMissingReceipt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.linemanOrderMissingReceipt),
      tap(action => this.logger.debug('@Effect Lineman Order Missing Receipt : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportLinemanOrderMissingReceipt(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportMobilePhoneSaleAndRefund$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.mobilePhoneSaleAndRefund),
      tap(action => this.logger.debug('@Effect Mobile Phone Sale and Refund : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportMobilePhoneSaleAndRefund(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportEdcPaymentTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.edcPaymentTransaction),
      tap(action => this.logger.debug('@Effect EDC Payment Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitListExceeded(payload?.storeCodes, 100, 'stores');
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportEdcPaymentTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportRefundExceptionTransaction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.refundExceptionTransaction),
      tap(action => this.logger.debug('@Effect Refund Exception Transaction : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }

        return this.newReportService.exportRefundExceptionTransaction(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  returnToSupplier$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.returnToSupplier),
      tap(action => this.logger.debug('@Effect Return to Supplier : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportReturnToSupplier(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  exportCustomerSatisfactionSurvey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.customerSatisfactionSurvey),
      tap(action => this.logger.debug('@Effect Customer SatisfactionS urvey : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateOrderLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportCustomerSatisfactionSurvey(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  agentPaymentTransactionReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.agentPaymentTransactionReport),
      tap(action =>
        this.logger.debug('@Effect Agent Payment Transaction Report : ' + JSON.stringify(action.payload))
      ),
      switchMap(({ payload }) => {
        const validateLimitStore = this.validateLimitStore(payload?.storeCodes);
        if (validateLimitStore) {
          return validateLimitStore;
        }
        return this.newReportService.exportAgentPaymentTransactionReport(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private logger: NGXLogger,
    private storeService: StoreService,
    private holdAssortmentReportService: HoldAssortmentReportService,
    private groupOrderReportService: GroupOrderReportService,
    private memberTransactionRewardsService: MemberTransactionRewardsService,
    private awaitingAllocationForGroupOrderService: AwaitingAllocationForGroupOrderService,
    private storeReportService: StoreReportService,
    private z8HoldRequestService: Z8HoldRequestService,
    private receiveOrderService: ReceiveOrderService,
    private preOrderTransactionsReportService: PreOrderTransactionsReportService,
    private promotionCompensateReportService: PromotionCompensateReportService,
    private promotionReportService: PromotionReportService,
    private salesTransactionService: SalesTransactionReportService,
    private supplierCouponReconcileReportService: SupplierCouponReconcileReportService,
    private inventoryCountTransactionService: InventoryCountTransactionService,
    private voucherReportService: VoucherReportService,
    private destroyService: DestroyService,
    private specialPointCompensateService: SpecialPointCompensateService,
    private specialPointReportService: SpecialPointReportService,
    private taskAssignmentReportService: TaskAssignmentReportService,
    private toteReportService: ToteReportService,
    private returnToWarehouseOrderService: ReturnToWarehouseOrderService,
    private returnToWarehouseGRService: ReturnToWarehouseGRService,
    private returnToWarehouseChargeService: ReturnToWarehouseChargeService,
    private exportReportService: ExportReportService,
    private flashSaleDashboardReportService: FlashSaleDashboardReportService,
    private onlineCouponTransactionService: OnlineCouponTransactionService,
    private onlineCouponCompensateService: OnlineCouponCompensateService,
    private rewardRedeemTransactionReportService: RewardRedeemTransactionReportService,
    private rewardOfferTransactionReportService: RewardOfferTransactionReportService,
    private salesTransactionConfidentialReportService: SalesTransactionConfidentialReportService,
    private claimService: ClaimReportService,
    private stockInformationService: StockInformationService,
    private profitSharingService: ProfitSharingService,
    private storeExportService: StoreExportService,
    private storeLocationReportService: StoreLocationReportService,
    private preorderReportService: PreOrderReportService,
    private holdOrderReportService: HoldOrderReportService,
    private pointEarnTransactionReportService: PointEarnTransactionReport,
    private purchaseOrderService: PurchaseOrderService,
    private promotionV2ReportService: PromotionV2ReportService,
    private promotionTransactionReportService: PromotionTransactionReportService,
    private materialRequestTransactionService: MaterialRequestTransactionService,
    private newReportService: NewReportService,
    private stockCountReportService: StockCountReportService,
    private giftWithPurchaseService: GiftWithPurchaseService
  ) {}

  loadingGroupByWarehouse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dynamicReportAction.loadingGroupByWarehouse),
      tap(action => this.logger.debug('@Effect Loading Group By Warehouse : ' + JSON.stringify(action.payload))),
      switchMap(({ payload }) => {
        return this.newReportService.exportLoadingGroupByWarehouse(payload).pipe(
          map(response => {
            const message = environment.alert.myReportRequest.replace('{request_no}', response.requestNo);
            return dynamicReportAction.exportReportResponse({
              isSuccess: true,
              response: { message }
            });
          }),
          catchError(error => of(dynamicReportAction.exportReportFileError({ isSuccess: false, error })))
        );
      })
    );
  });

  timeToExport(timeFormat: string): string {
    return moment().format(timeFormat);
  }

  validateLimitStore<T>(stores: T[]) {
    if (stores?.length > 100) {
      return of(
        dynamicReportAction.exportReportError({
          isSuccess: false,
          error: {
            error: {
              message: 'Export limit exceeded (up to 100 stores).'
            }
          }
        })
      );
    }
    return;
  }

  validateOrderLimitStore(stores) {
    if (stores?.length > 100) {
      return of(
        dynamicReportAction.exportReportError({
          isSuccess: false,
          error: {
            error: {
              message: 'Export limit exceeded (up to 100 stores).'
            }
          }
        })
      );
    }
    return;
  }

  validateLimitListExceeded<T>(items: T[], limit: number, limitType: string) {
    if (items && items.length > limit) {
      return of(
        dynamicReportAction.exportReportError({
          isSuccess: false,
          error: {
            error: {
              message: `Export limit exceeded (up to ${limit} ${limitType}).`
            }
          }
        })
      );
    }

    return;
  }

  convertArrayFilter(value) {
    return value && value.length > 0 ? value.toString() : null;
  }
}
